import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getPasswordCriteria, PasswordCriteriaList } from 'components/ui/PasswordCriteriaList';
import { confirmSignIn, signOut } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { changePasswordOneTimeSchema } from 'schemas/changePasswordSchema';

type ChangePasswordFormValues = z.infer<typeof changePasswordOneTimeSchema>;

interface Props {
  onSuccess: () => void;
  onBack: () => void;
}

export const ChangePassword = ({ onSuccess, onBack }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordOneTimeSchema),
    mode: 'onChange',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPasswordValue = watch('newPassword');
  const confirmPasswordValue = watch('confirmPassword');

  const allCriteriaMet = Object.values(getPasswordCriteria(newPasswordValue)).every(Boolean);
  const passwordsMatch = newPasswordValue === confirmPasswordValue;

  const handleChangePassword = async ({ newPassword }: ChangePasswordFormValues) => {
    setLoading(true);
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      await signOut();
      toast.success(t('toast.passwordUpdated'));
      onSuccess();
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || t('toast.failedChangePassword'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit(handleChangePassword)}>
      <p className="text-sm text-slate-500">{t('changePassword.mustSetPassword')}</p>

      <div className="space-y-2">
        <Label htmlFor="newPassword">{t('changePassword.newPassword')}</Label>
        <Input
          id="newPassword"
          isPassword
          placeholder={t('changePassword.newPasswordPlaceholder')}
          disabled={loading}
          {...register('newPassword')}
        />
        {errors.newPassword && (
          <p className="text-xs text-red-500">{t(errors.newPassword.message ?? '')}</p>
        )}
      </div>

      <PasswordCriteriaList password={newPasswordValue} />

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">{t('changePassword.confirmPassword')}</Label>
        <Input
          id="confirmPassword"
          isPassword
          placeholder={t('changePassword.confirmPasswordPlaceholder')}
          disabled={loading}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p className="text-xs text-red-500">{t(errors.confirmPassword.message ?? '')}</p>
        )}
      </div>

      <div className="space-y-3">
        <Button
          type="submit"
          className="h-12 w-full rounded-2xl text-base font-semibold"
          disabled={!allCriteriaMet || !passwordsMatch || !confirmPasswordValue || loading}
        >
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
          {loading ? t('changePassword.updating') : t('changePassword.setNewPassword')}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="h-12 w-full rounded-2xl text-base"
          disabled={loading}
          onClick={onBack}
        >
          {t('changePassword.back')}
        </Button>
      </div>
    </form>
  );
};
